import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, exhaustMap, switchMap, retry } from 'rxjs/operators';
import { StandingAuthorizationsResponse, StandingAuthorization } from '../models/Standing-Authorizations';
import { StandingAuthorizationsService } from '../services/standing-authorizations.service';
import { TransactionFormActions, StandingAuthorizationsActions, StandingAuthorizationsTableActions } from '../actions';
import { achRecTypeCodes, InstructionStatusCdDict, MediaTypeCdDict, MediaTypeCode, transactionDirection } from '../../shared/utilities/Constants';
import { MediaTypeCd, TransactionType } from '../../shared/types/Transaction';
import { StandingInstruction } from '../../shared/types/StandingInstruction';
import { selectIsFromEdit } from '../reducers';
import { Store } from '@ngrx/store';
import { ServiceError } from '../models/shared/ServiceError';
import { StatusViewService } from '../../shared/services/status-view.service';
import { CancelResponse, CancelTransactionDetails } from '../../shared/models/CancelTransactionDetails';
import { CashieringTransaction, RecurringTransaction, Sloa } from '../models';
import { selectInfo } from '../../account-info-header/reducers';
import { concatLatestFrom } from "@ngrx/operators";

@Injectable()
export class StandingAuthorizationsEffects {
  constructor(
    private actions$: Actions,
    private standingAuthService: StandingAuthorizationsService,
    private statusViewService: StatusViewService,
    private store: Store
  ) {}

  loadSloas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthorizationsActions.loadSloas),
      concatLatestFrom(() => [
        this.store.select(selectIsFromEdit)
      ]),
      exhaustMap(([action, isFromEdit]) => {
        if (isFromEdit) {
          return of(TransactionFormActions.editLoading());
        }

        return this.standingAuthService.callStandingAuthorizations(action).pipe(
          map((standingAuthorizationsResponse: StandingAuthorizationsResponse) => {
            const instructions = standingAuthorizationsResponse.data.map((sloa) => {
              return createInstructionFromStandingAuthorizations(sloa.attributes, action.sloaType);
            });

            return StandingAuthorizationsActions.loadSloasSuccess({ instructions, transactionType: requestTransactionTypeDict[action.sloaType]});
          }),
          catchError((err: ServiceError) =>
            of(StandingAuthorizationsActions.loadSloasFailure(err))
          )
        )
      })
    )
  );

  callCancelTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StandingAuthorizationsTableActions.deleteTransactionClicked),
            concatLatestFrom(() => [
                this.store.select(selectInfo)
            ]),
            exhaustMap(([action, accountInfo]) => {
                return this.statusViewService.cancelTransaction(
                    this.mapCancelRequest(action.transaction, accountInfo.accountId))
                    .pipe(
                        retry(action.shouldRetry ? 1 : 0),
                        switchMap((res: CancelResponse) => {
                          if (res.message.endsWith("Request(s) pending for this Set-Up. Request(s) must process or be canceled before deleting Set-Up.")){
                            return of(StandingAuthorizationsTableActions.deleteTransactionFailurePendingTransaction({
                                errorMsg: res.message
                            }));
                          }
                          else{
                            return of(StandingAuthorizationsTableActions.deleteTransactionSuccess({ isNewAchProfile: action.isNewAchProfile ?? false }));
                          }
                        }),
                        catchError(ex => {
                            return of(StandingAuthorizationsTableActions.deleteTransactionFailure({
                                errorMsg: ex.error.message
                            }));
                        })
                )
            })
        )
    );
    mapCancelRequest(
      transaction: CashieringTransaction | RecurringTransaction | Sloa,
      accountId: string
    ) : CancelTransactionDetails {
      let transactionType: 'JournalSloa' | '';
      let mediaTypeCd = this.getMediaTypeCdFromTransactionType(transaction);
      transactionType = (transaction.mediaTypeCd === MediaTypeCode.J) ? 'JournalSloa' : '';
      return {
          cancelRequest: {
              cancelReasonCode: '',
              cashieringRequestId: transaction.cashieringRqstId.toString() ?? '',
              cashieringSecRequestId: transaction.cashieringRqstSecondId,
              masterAccountId: transaction.faMasterAcct,
              mediaTypeCd: mediaTypeCd,
              subAccountId: accountId,
              transactionType: transactionType
          }
      };
  }

  getMediaTypeCdFromTransactionType(transaction: CashieringTransaction | RecurringTransaction | Sloa): string{
    var mediaTypeCd: string = '';
    switch (transaction.mediaTypeCd){
      case (MediaTypeCode.J):
        mediaTypeCd = transaction.mediaTypeCd;
        break;
      case (MediaTypeCode.C):
        mediaTypeCd = transaction.mediaTypeCd;
        break;
      case (MediaTypeCode.A || MediaTypeCode.ACH):
        mediaTypeCd = MediaTypeCode.ASC;
        break;
      case (MediaTypeCode.W):
        mediaTypeCd = MediaTypeCode.WSC;
        break;
    }
    return mediaTypeCd;
  }
}

const requestTransactionTypeDict: {[key: string]: TransactionType } = {
  C: 'check',
  W: 'wire',
  A: 'ach',
  J: 'internal-transfer',
  bundledwire: 'wire',
  bundledcheck: 'check'
};

function createInstructionFromStandingAuthorizations(sloa: StandingAuthorization, sloaType: string): StandingInstruction {
  return {
    nickname: sloa.nickname,
    cashieringRqstId: sloa.cashieringRqstId.toString(),
    cashieringRqstSecondId: sloa.cashieringRqstSecondId.toString(),
    fundsDirection: sloa.fundsDirection as transactionDirection,
    transactionType: MediaTypeCdDict[sloa.txnCd as MediaTypeCd],
    txnCode: mapTxnCode(sloa.txnCd, sloa.isRecurring),
    to1: sloa.to1,
    to2: sloa.to2,
    to3: sloa.to3,
    address: mapAddress(sloa),
    statusCd: sloa.isActive ? InstructionStatusCdDict.active : InstructionStatusCdDict.inactive,
    masterAccountId: sloa.masterAccountId,
    isThirdParty: sloa.isThirdPartyInd === 'Y',
    isBundled: sloaType === 'bundledwire' || sloaType === 'bundledcheck' ? true : false,
    isStandingInstruction: false,
    mediaTypeCd: sloa.txnCd,
    isRecurring: sloa.isRecurring ?? false
  };
}

function mapTxnCode(txnCd: string, isRecurring: boolean): string | undefined {
  if (txnCd === 'A') {
    return isRecurring ? achRecTypeCodes.recurring : achRecTypeCodes.onRequest;
  }
  return undefined;
}

function mapAddress(sloa: StandingAuthorization): string {
  if (sloa.txnCd === 'A' && sloa.isRecurring) {
    return `${sloa.to1?.trim()} ${sloa.to2?.trim()} ${sloa.to3?.trim()}`.trim();
  }
  return `${sloa.address1?.trim()} ${sloa.address2?.trim()} ${sloa.address3?.trim()}`.trim();
}
